import _typeof from "@babel/runtime/helpers/typeof";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _isNil from "lodash/isNil";
var _excluded = ["driverGroupConfig", "driverGroup"];

var _templateObject, _templateObject2;

import "core-js/modules/es6.regexp.match.js";
import "core-js/modules/es6.number.constructor.js";
import "core-js/modules/es6.array.filter.js";
import "core-js/modules/es6.regexp.replace.js";
import "core-js/modules/es6.regexp.constructor.js";
import React from 'react';
import { gql } from '@nerdwallet/apollo';
import { getDisplayName as getName, getImageAltText, getImageSrc, getMonetizable as deriveMonetizationStatus } from '@nerdwallet/marketplace/selectors/common';
import { MONETIZATION_STATUSES } from '@nerdwallet/marketplace/constants';
import { getCreditScore, isSignedIn } from '@nerdwallet/js-auth-tools/selectors'; // @todo figure out how to not rely on nw-client-lib

import { getBucketFromScore, BucketName } from '@nerdwallet/nw-client-lib/credit-score';
import { STATIC_IMAGE_HEIGHT, STATIC_IMAGE_WIDTH, DEFAULT_DRIVER_GROUP, LOA_CTA_TOOLTIP_WITH_ARTICLE } from '../constants';
import { sanitizeNode } from './render';
export { DEFAULT_DRIVER_GROUP, sanitizeNode };
var KETO_CARD_BREAKPOINT = 999; // @imageWrapperBreakpointMax

var MPC_BREAKPOINT = 766; // @screen-medium

var MPC_IMAGE_MOBILE_WIDTH = 160; // @imageWidthMobile

var MAX_IMAGE_WIDTH_DESKTOP = 221; // @imageWidthDesktop

var MAX_IMAGE_HEIGHT = 140; // @imageMaxHeight

var KETO_IMAGE_MOBILE_WIDTH = 140; // @imageWidthMobile

var WIDTH = 'width';
var HEIGHT = 'height';
var ImgSrcDimensionsPattern = /^.*_(\d+)x(\d+)(_[ML])?\.\w\w\w\w?$/;
export var IMAGE_RESIZABLE_ROOT = 'https://www.nerdwallet.com/cdn-cgi/image/quality=85,format=auto';

/**
 * Given a dimensions object with a height and width, return the resized dimensions object.
 */
var resizeImageGivenOneDimension = function resizeImageGivenOneDimension(dimensions, fixedDimension, fixedValue) {
  var ratio = dimensions.width / dimensions.height;

  switch (fixedDimension) {
    case WIDTH:
      return {
        height: Math.round(fixedValue / ratio),
        width: fixedValue
      };

    case HEIGHT:
      return {
        height: fixedValue,
        width: Math.round(fixedValue * ratio)
      };

    default:
      return dimensions;
  }
};
/** Given the source of the image figure out the dimensions.
 */


export var getImageDimensions = function getImageDimensions(src) {
  if (typeof src !== 'string') {
    return {};
  }

  var tokens = src.match(ImgSrcDimensionsPattern);

  if (!tokens) {
    return {};
  }

  var height = Number(tokens[1]);
  var width = Number(tokens[2]); // eslint-disable-next-line no-restricted-globals

  if (isNaN(height) || isNaN(width)) {
    return {};
  }

  return {
    height: height,
    width: width
  };
};
/** Get dimensions by width and the default aspect ratio
 *
 * @param {Object} dimensions
 * @param {string} width
 *
 * @returns {Object} - {height, width}
 *
 */

export var getDimensionsByWidth = function getDimensionsByWidth(width) {
  return {
    height: Math.round(width * MAX_IMAGE_HEIGHT / MAX_IMAGE_WIDTH_DESKTOP),
    width: width
  };
};
/**
 * @typedef {Object} MarketplaceEntityImageData
 * @property {string} src
 * @property {string} alt
 * @property {number} breakpoint
 * @property {Object} imageDimensions - { height: number, width: number}
 * @property {Object} imageDimensionsMobile - { height: number, width: number}
 * @property {string} sizes
 * @property {string} srcSet
 */

export var getLazyLoadableImageData = function getLazyLoadableImageData(_ref) {
  var imageData = _ref.imageData,
      imageDataMobile = _ref.imageDataMobile,
      imageDimensions = _ref.imageDimensions,
      imageDimensionsMobile = _ref.imageDimensionsMobile,
      imageRawDimensions = _ref.imageRawDimensions,
      isActive = _ref.isActive,
      isKetoCard = _ref.isKetoCard;

  if (!(imageRawDimensions !== null && imageRawDimensions !== void 0 && imageRawDimensions.height) || !(imageRawDimensions !== null && imageRawDimensions !== void 0 && imageRawDimensions.width) || !isActive) {
    // if conditions for lazy loading are not met, load image normally
    return imageData;
  }

  var mobileImageDimensions = imageDimensionsMobile;
  var dimensions = imageDimensions; // fill missing image height

  if (!imageDimensions.height) {
    dimensions = getDimensionsByWidth(imageDimensions.width);
  }

  if (!imageDimensionsMobile.height) {
    mobileImageDimensions = getDimensionsByWidth(imageDimensionsMobile.width);
  }

  var breakpoint = KETO_CARD_BREAKPOINT;
  var mobileWidth = KETO_IMAGE_MOBILE_WIDTH;

  if (!isKetoCard) {
    // keto card's mobile image matches the standard medium card image size of 140px
    // mpc uses a width of 160px, so we need to use the ratio to calculate it's height
    mobileImageDimensions = resizeImageGivenOneDimension(imageDimensionsMobile, WIDTH, MPC_IMAGE_MOBILE_WIDTH);
    breakpoint = MPC_BREAKPOINT;
    mobileWidth = MPC_IMAGE_MOBILE_WIDTH;
  } // when the image's height is larger than our max image height, need to calculate the relative width


  if (dimensions.height > MAX_IMAGE_HEIGHT) {
    dimensions = resizeImageGivenOneDimension(dimensions, HEIGHT, MAX_IMAGE_HEIGHT);
  }

  if (mobileImageDimensions.height > MAX_IMAGE_HEIGHT) {
    mobileImageDimensions = resizeImageGivenOneDimension(mobileImageDimensions, HEIGHT, MAX_IMAGE_HEIGHT);
  }

  var srcSet = '';
  var sizes = '';

  if (dimensions.width && mobileImageDimensions.width) {
    srcSet = "".concat(imageData.src, " ").concat(dimensions.width, "w, ").concat(imageDataMobile.src, " ").concat(mobileImageDimensions.width, "w");
    sizes = "(max-width: ".concat(breakpoint, "px) ").concat(mobileWidth, "px, ").concat(MAX_IMAGE_WIDTH_DESKTOP, "px");
  }

  return _objectSpread(_objectSpread({}, imageData), {}, {
    breakpoint: breakpoint,
    imageDimensions: dimensions,
    imageDimensionsMobile: mobileImageDimensions,
    imageRawDimensions: imageRawDimensions,
    sizes: sizes,
    srcSet: srcSet
  });
};
/**
 * Uses the marketplace entity to create resizable image paths if available. Otherwise,
 * paths will be returned based on `sourceMedium` and `sourceLarge`.
 */

export var getResizableImageSources = function getResizableImageSources(image) {
  var _image$sourceMedium, _image$sourceLarge;

  var imageDataMobile = {
    src: (_image$sourceMedium = image === null || image === void 0 ? void 0 : image.sourceMedium) !== null && _image$sourceMedium !== void 0 ? _image$sourceMedium : image === null || image === void 0 ? void 0 : image.source,
    alt: image === null || image === void 0 ? void 0 : image.alt
  };
  var imageData = {
    src: (_image$sourceLarge = image === null || image === void 0 ? void 0 : image.sourceLarge) !== null && _image$sourceLarge !== void 0 ? _image$sourceLarge : image === null || image === void 0 ? void 0 : image.source,
    alt: image === null || image === void 0 ? void 0 : image.alt
  };

  if (image !== null && image !== void 0 && image.resizableImagePath) {
    imageDataMobile.src = "".concat(IMAGE_RESIZABLE_ROOT, ",width=140/").concat(image.resizableImagePath);
    imageData.src = "".concat(IMAGE_RESIZABLE_ROOT, ",width=600/").concat(image.resizableImagePath);
  }

  return {
    imageDataMobile: imageDataMobile,
    imageData: imageData
  };
};
/**
 * Wrapper around getImageData which augments image data with lazy load information.
 */

export var getKetoCardLazyLoadableImageData = function getKetoCardLazyLoadableImageData(marketplaceEntity) {
  var status = marketplaceEntity.status,
      image = marketplaceEntity.image;

  var _getResizableImageSou = getResizableImageSources(image),
      imageData = _getResizableImageSou.imageData,
      imageDataMobile = _getResizableImageSou.imageDataMobile;

  var isActive = status === 'ACTIVE'; // for keto card both of these return an empty array since the graphql url won't have the dimensions as before.

  var imageDimensions = getImageDimensions(imageData.src);
  var imageDimensionsMobile = getImageDimensions(imageDataMobile.src); // as a result this will always return the imageData since it will never be lazy loaded

  return getLazyLoadableImageData({
    imageData: imageData,
    imageDataMobile: imageDataMobile,
    imageDimensions: imageDimensions,
    imageDimensionsMobile: imageDimensionsMobile,
    isActive: isActive,
    isKetoCard: true
  });
};
getKetoCardLazyLoadableImageData.fragment = gql(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  fragment getLazyLoadableImageData on MarketplaceEntityInterface {\n    status\n    ... on MarketplaceEntityWithPlacementInterface {\n      image {\n        alt\n        resizableImagePath\n        sourceMedium\n        sourceLarge\n      }\n    }\n  }\n"])));
/**
 * Lightweight utility for conventional representation of a product's overarching section category.
 */

export var buildTrkSectionCategory = function buildTrkSectionCategory() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  return args.filter(Boolean).join('__');
};
/**
 * Utility for retrieving image data from raw marketplace entity.
 */

export var getImageData = function getImageData(marketplaceEntity) {
  var srcSize = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  return {
    src: getImageSrc(marketplaceEntity, srcSize),
    alt: getImageAltText(marketplaceEntity) || getName(marketplaceEntity)
  };
};
/**
 * Utility for mapping legacy image data from the EntityPlacementImage.
 */

export var getImageDataFromEntityPlacementImage = function getImageDataFromEntityPlacementImage(image) {
  var resizable = getResizableImageSources(image);
  return _objectSpread(_objectSpread({}, resizable.imageData), {}, {
    imageDimensions: getDimensionsByWidth(MAX_IMAGE_WIDTH_DESKTOP),
    imageDimensionsMobile: getDimensionsByWidth(MPC_IMAGE_MOBILE_WIDTH),
    imageRawDimensions: {
      height: image === null || image === void 0 ? void 0 : image.height,
      width: image === null || image === void 0 ? void 0 : image.width
    }
  });
};
getImageDataFromEntityPlacementImage.fragment = gql(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  fragment getImageDataFromEntityPlacementImage on EntityPlacementImage {\n    alt\n    height\n    resizableImagePath\n    source\n    sourceLarge\n    sourceMedium\n    width\n  }\n"])));
/**
 * Tracking util for building the entity name for the Bedrock analytics pipeline.
 */

export var buildAnalyticsEntityName = function buildAnalyticsEntityName(entityName) {
  return entityName === null || entityName === void 0 ? void 0 : entityName.toLowerCase().replace(/ |\W/g, '_').substring(0, 64);
};
/**
 * Gets the expected shopping finished event monetizable string
 * One of 'yes_assumed',
 *        'no',
 *        'no_because_of_limit',
 *        'no_because_of_lead',
 */

export var getMonetizable = function getMonetizable(marketplaceEntity) {
  return deriveMonetizationStatus(marketplaceEntity) || MONETIZATION_STATUSES.YES_ASSUMED;
};
/**
 * Gets the drivers to use based off a config object, an optional driver group name,
 * and any other props passed to the config functions.  It tries to fallback to
 * any declared default configuration, then if it still can't find it, to no drivers.
 * @deprecated.
 */

export var getDrivers = function getDrivers(_ref2) {
  var driverGroupConfig = _ref2.driverGroupConfig,
      driverGroup = _ref2.driverGroup,
      props = _objectWithoutProperties(_ref2, _excluded);

  if (_typeof(driverGroupConfig) === 'object') {
    var specificGroupGenerator = driverGroupConfig[driverGroup];

    if (typeof specificGroupGenerator === 'function') {
      return specificGroupGenerator(props);
    }

    var defaultGroupGenerator = driverGroupConfig[DEFAULT_DRIVER_GROUP];

    if (typeof defaultGroupGenerator === 'function') {
      return defaultGroupGenerator(props);
    }
  }

  return [];
};
/**
 * eslint-enable camelcase
 */

/**
 * Checks that the user is signed in and has an excellent credit score
 */

export var isSignedInAndCreditScoreExcellent = function isSignedInAndCreditScoreExcellent(identity) {
  // if the user is logged in
  if (isSignedIn(identity)) {
    // get the credit score to determine the credit bucket
    var creditScore = getCreditScore(identity); // If the user has a credit score and the credit score bucket
    // is "excellent", ONLY then display PRE-QUAL CTA for the user else hide it

    return creditScore && getBucketFromScore(creditScore) === BucketName.excellent;
  }

  return false;
};
/** Helper function to add height and width to the url
 *
 * @param                   Object
 * @key url                 string  base link
 * @key height              string  request image height
 * @key width               string  request image width
 *
 * @return                  string  new url with height and width appended
 */

export var addSizes = function addSizes(_ref3) {
  var _src$match$, _src$match, _params$match$, _params$match;

  var height = _ref3.height,
      width = _ref3.width,
      _ref3$src = _ref3.src,
      src = _ref3$src === void 0 ? '' : _ref3$src;
  var regexWithParam = new RegExp('/cdn-cgi/image/(.*?){1}[0-9]/');
  var regexWithoutParam = new RegExp('/cdn-cgi/image//');
  var params = (_src$match$ = (_src$match = src.match(regexWithParam)) === null || _src$match === void 0 ? void 0 : _src$match[0]) !== null && _src$match$ !== void 0 ? _src$match$ : '';
  var qualityParam = (_params$match$ = (_params$match = params.match(new RegExp('quality=(.*?)[0-9]*'))) === null || _params$match === void 0 ? void 0 : _params$match[0]) !== null && _params$match$ !== void 0 ? _params$match$ : '';
  var heightParam = height ? "height=".concat(height, ",") : '';
  var widthParam = width ? "width=".concat(width, ",") : '';
  return src.replace(params ? regexWithParam : regexWithoutParam, "/cdn-cgi/image/".concat(heightParam).concat(widthParam).concat(qualityParam, ",format=auto/"));
};
/** Helper function to get sizes
 *
 * @param                    Object
 * @return                   string   new url with height and width appended
 */

export var getSrcWithDimensions = function getSrcWithDimensions(_ref4) {
  var _ref4$imageRawDimensi = _ref4.imageRawDimensions,
      imageRawDimensions = _ref4$imageRawDimensi === void 0 ? {} : _ref4$imageRawDimensi,
      _ref4$src = _ref4.src,
      src = _ref4$src === void 0 ? '' : _ref4$src;
  var height = imageRawDimensions.height,
      width = imageRawDimensions.width;
  var placeholderWidth;
  var placeholderHeight;

  if (width > height) {
    // landscape
    placeholderWidth = STATIC_IMAGE_WIDTH;
    placeholderHeight = STATIC_IMAGE_WIDTH * height / width;
  } else {
    // portrait
    placeholderHeight = STATIC_IMAGE_HEIGHT;
    placeholderWidth = STATIC_IMAGE_HEIGHT / height * width;
  }

  return addSizes({
    height: placeholderHeight,
    width: placeholderWidth,
    src: src
  });
};
/* eslint-disable no-underscore-dangle */

export var isCCOffer = function isCCOffer(marketplaceEntity) {
  return (marketplaceEntity === null || marketplaceEntity === void 0 ? void 0 : marketplaceEntity.__typename) === 'CreditCardsOfferV2';
};
/* eslint-enable no-underscore-dangle */

/** Function to get Loa CTA tooltip content
 *
 * @param                    Identity
 * @return                   string   Tooltip content copy
 */

export var getLoaTooltipContent = function getLoaTooltipContent(identity) {
  var approvalOddsEnabled = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  return !(identity !== null && identity !== void 0 && identity.isSignedIn) || _isNil(getCreditScore(identity)) || !approvalOddsEnabled ? LOA_CTA_TOOLTIP_WITH_ARTICLE : /*#__PURE__*/React.createElement(React.Fragment, null);
};